import React, { createContext, useContext, useRef, useCallback, PropsWithChildren } from 'react';

interface TemplateCardRefsContextType {
  registerCard: (id: string, element: HTMLElement | null) => void;
  getCard: (id: string) => HTMLElement | undefined;
  getContainer: () => HTMLElement | undefined;
  registerContainer: (element: HTMLElement | null) => void;
}

const TemplateCardRefsContext = createContext<TemplateCardRefsContextType | null>(null);

export function TemplateCardRefsProvider({ children }: PropsWithChildren): React.ReactElement {
  const cardsMap = useRef<Map<string, HTMLElement>>(new Map());
  const containerRef = useRef<HTMLElement | null>(null);

  const registerCard = useCallback((id: string, element: HTMLElement | null) => {
    if (element) {
      cardsMap.current.set(id, element);
    } else {
      cardsMap.current.delete(id);
    }
  }, []);

  const getCard = useCallback((id: string) => {
    return cardsMap.current.get(id);
  }, []);

  const registerContainer = useCallback((element: HTMLElement | null) => {
    containerRef.current = element;
  }, []);

  const getContainer = useCallback(() => {
    return containerRef.current ?? undefined;
  }, []);

  return (
    <TemplateCardRefsContext.Provider 
      value={{ registerCard, getCard, getContainer, registerContainer }}
    >
      {children}
    </TemplateCardRefsContext.Provider>
  );
}

export function useTemplateCardRefs(): TemplateCardRefsContextType {
  const context = useContext(TemplateCardRefsContext);
  if (!context) {
    throw new Error(
      'useTemplateCardRefs must be used within TemplateCardRefsProvider'
    );
  }
  return context;
}

